import { Button } from '@/app/common/components/button/Button'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { inputVariants } from '@/app/common/components/input/inputVariants'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { currencyFormatter } from '@/lib/utils'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { DollarSignIcon, ArrowRightLeft, MinusCircle } from 'lucide-react'
import { useState } from 'react'
import CurrencyInput from 'react-currency-input-field'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { PURSE_KEYS } from '@/queries/program_purses/keys.ts'
import { ProgramPurseResource } from '@/queries/program_purses/schemas.ts'
import { TransferFundsDialog } from '@/app/pages/program_purse_settings/purse-details/_components/transfer-funds-dialog.tsx'
import { RemoveFundsDialog } from '@/app/pages/program_purse_settings/purse-details/_components/remove-funds-dialog.tsx'

// Simple CardTitle component
const CardTitle = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => <h3 className={`text-lg font-semibold ${className}`}>{children}</h3>

// Zod schema for deposit form
const DepositSchema = z.object({
  depositAmount: z
    .string()
    .min(1, 'Amount is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
      message: 'Amount must be a positive number',
    })
    .refine((val) => Number(val) <= 100000000, {
      message: 'Amount cannot exceed $100,000,000',
    }),
})
type DepositFormValues = z.infer<typeof DepositSchema>

interface FinancialSummaryProps {
  purse: ProgramPurseResource
  programId: number
}

export function FinancialSummary({ purse, programId }: FinancialSummaryProps) {
  const [isDepositDialogOpen, setIsDepositDialogOpen] = useState(false)
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false)
  const [isRemoveFundsDialogOpen, setIsRemoveFundsDialogOpen] = useState(false)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // React Hook Form for deposit
  const {
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<DepositFormValues>({
    resolver: zodResolver(DepositSchema),
    defaultValues: { depositAmount: '' },
  })

  // Tanstack query mutation for deposit
  const depositMutation = useMutation({
    mutationFn: async (amount: number) => {
      return await axios.post(
        `/programs/${programId}/purses/${purse.id}/deposit`,
        {
          amount,
          sender: '',
        }
      )
    },
    onSuccess: (_, variables) => {
      toast({
        variant: 'default',
        title: 'Deposit Successful',
        description: `Successfully deposited ${currencyFormatter.format(
          variables
        )} into purse ${purse.name}.`,
      })
      reset()
      setIsDepositDialogOpen(false)
      // Invalidate or update the purse details query so the new balance is shown

      queryClient.invalidateQueries({})

      queryClient.invalidateQueries({
        queryKey: PURSE_KEYS.PURSE_DETAILS(String(purse.id), programId),
      })
      queryClient.invalidateQueries({
        queryKey: PURSE_KEYS.PURSES(programId),
      })
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Deposit Failed',
        description:
          'There was an error processing your deposit. Please try again.',
      })
    },
  })

  // Form submit handler for deposit
  const onDepositSubmit = (values: DepositFormValues) => {
    const amount = Number(values.depositAmount.replace(/[^\d.-]+/g, ''))
    depositMutation.mutate(amount)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <CardTitle>Financial Summary</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsRemoveFundsDialogOpen(true)}
          >
            Remove Funds
            <MinusCircle className="size-4 text-neutral-800 ml-2" />
          </Button>
          <RemoveFundsDialog
            open={isRemoveFundsDialogOpen}
            onOpenChange={setIsRemoveFundsDialogOpen}
            purse={purse}
            programId={programId}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsTransferDialogOpen(true)}
          >
            Transfer Funds
            <ArrowRightLeft className="size-4 text-neutral-800 ml-2" />
          </Button>
          <TransferFundsDialog
            open={isTransferDialogOpen}
            onOpenChange={setIsTransferDialogOpen}
            purse={purse}
            programId={programId}
          />
          <Dialog
            open={isDepositDialogOpen}
            onOpenChange={setIsDepositDialogOpen}
          >
            <DialogTrigger asChild>
              <Button variant={'outline'} size="sm">
                Add purse funds{' '}
                <DollarSignIcon className="size-4 text-neutral-800 ml-2" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form
                onSubmit={handleSubmit(onDepositSubmit)}
                className="space-y-4"
              >
                <div>
                  <Label htmlFor="deposit-amount">Amount</Label>
                  <CurrencyInput
                    id="deposit-amount"
                    name="depositAmount"
                    decimalsLimit={2}
                    min={1}
                    prefix="$"
                    value={watch('depositAmount')}
                    onValueChange={(value) =>
                      setValue('depositAmount', value || '', {
                        shouldValidate: true,
                      })
                    }
                    placeholder="Enter amount"
                    required
                    className={inputVariants({
                      variant: 'text',
                    })}
                  />
                  {errors.depositAmount && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.depositAmount.message}
                    </p>
                  )}
                </div>

                {/* Deposit Summary */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <h4 className="font-medium text-gray-900">Deposit Summary</h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current Total:</span>
                    <span className="font-medium">
                      {currencyFormatter.format(purse.total)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Deposit Amount:</span>
                    <span className="font-medium">
                      {watch('depositAmount')
                        ? currencyFormatter.format(
                            Number(
                              watch('depositAmount').replace(/[^\d.-]+/g, '')
                            ) || 0
                          )
                        : '$0.00'}
                    </span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-medium text-gray-900">
                      New Total:
                    </span>
                    <span className="font-bold text-green-600">
                      {currencyFormatter.format(
                        purse.total +
                          (Number(
                            watch('depositAmount')?.replace(/[^\d.-]+/g, '')
                          ) || 0)
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 items-center">
                  <div>
                    <SubmitButton
                      buttonTxt="Deposit"
                      status={depositMutation.status}
                    />
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {currencyFormatter.format(purse.total)}
            </div>
            <div className="text-sm text-gray-600">Deposited Funds</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {currencyFormatter.format(purse.balance_distribuited)}
            </div>
            <div className="text-sm text-gray-600">Total Distributed</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {currencyFormatter.format(purse.balance_available)}
            </div>
            <div className="text-sm text-gray-600">Remaining</div>
          </div>
        </div>
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-primary h-3 rounded-full"
              style={{
                width: `${purse.total > 0 ? (purse.balance_distribuited / purse.total) * 100 : 0}%`,
              }}
            ></div>
          </div>
          <div className="text-center text-sm text-gray-600 mt-2">
            {purse.total > 0
              ? ((purse.balance_distribuited / purse.total) * 100).toFixed(1)
              : '0'}
            % distributed
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
